import { formatDay, tt } from '@agos/shared'

/**
 * *Tvoji odporučení* — who used this customer's code, and what it earned them.
 *
 * `GET /customer-auth/me/referrals` answers three totals and a page of rows: who was referred, whether
 * the referral has been **rewarded** yet, how many points it paid, and when. Legacy shows all of it —
 * a three-number strip, a row per person, and *Načítať ďalších 20*.
 *
 * This port showed **the count and nothing else** (FB-C57). A number on its own cannot answer the
 * question somebody opens this section to ask, which is *did my friend actually order, and did I get
 * the points*. Both of those are per-row facts.
 *
 * **A referred person who has not ordered yet is not a failure**, and legacy is careful about it:
 * *Čaká na prvú objednávku* rather than a zero or a cross. The reward is owed, not refused.
 */

export interface Referral {
  id: number
  /** The referred customer's name, or `null` — they may have ordered as a guest. */
  name: string | null
  rewarded: boolean
  rewardPoints: number
  createdAt: string
}

export interface ReferralActivity {
  referrals: Referral[]
  totalCount: number
  rewardedCount: number
  totalPoints: number
  hasMore: boolean
}

function whole(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.trunc(value) : 0
}

export function referralActivityOf(payload: unknown): ReferralActivity {
  const row = (typeof payload === 'object' && payload !== null ? payload : {}) as Record<string, unknown>
  const rows = Array.isArray(row.referrals) ? row.referrals : []
  return {
    referrals: rows.flatMap((entry) => {
      if (typeof entry !== 'object' || entry === null) return []
      const item = entry as Record<string, unknown>
      if (typeof item.id !== 'number') return []
      const name = typeof item.name === 'string' ? item.name.trim() : ''
      const referredName = typeof item.referredName === 'string' ? item.referredName.trim() : ''
      return [
        {
          id: item.id,
          name: referredName !== '' ? referredName : name !== '' ? name : null,
          rewarded: item.status === 'rewarded',
          rewardPoints: whole(item.rewardPoints),
          createdAt: typeof item.createdAt === 'string' ? item.createdAt : '',
        },
      ]
    }),
    totalCount: whole(row.totalCount),
    rewardedCount: whole(row.rewardedCount),
    totalPoints: whole(row.totalPoints),
    hasMore: row.hasMore === true,
  }
}

/** What a row says on the right: the points earned, or that they are still owed. */
export function referralNote(referral: Referral): string {
  // Legacy prints `+N b.` for a rewarded one and an italic *waiting* for the rest. Zero points on a
  // rewarded referral is a real state — a campaign can reward with a coupon rather than points — so it
  // says *rewarded* rather than *+0 b.*, which would read as nothing having happened.
  if (!referral.rewarded) return tt('Čaká na prvú objednávku')
  return referral.rewardPoints > 0 ? `+${String(referral.rewardPoints)} b.` : tt('Odmenené')
}

/** The name to print. Somebody who ordered as a guest has none, and legacy says so rather than blanking. */
export function referralName(referral: Referral): string {
  return referral.name ?? tt('Anonymný používateľ')
}

/** When it happened, in the customer's own date format. */
export function referralDate(referral: Referral): string {
  return referral.createdAt === '' ? '' : formatDay(referral.createdAt)
}

/**
 * Whether there is anything to show at all.
 *
 * `totalCount` rather than `referrals.length`: a customer on page two of a list they then filtered
 * down to nothing still has referrals, and the empty state would be a lie.
 */
export function hasReferrals(activity: ReferralActivity): boolean {
  return activity.totalCount > 0
}

/**
 * *Odporúčaj priateľa* — this customer's own code, and what sharing it is worth.
 *
 * `GET /customer/referral` answers the **code**, a ready-made **link**, and the two halves of the deal:
 * what the friend saves on their first order and what this customer earns for it. It is a different
 * endpoint from `me/referrals`, which lists the people already referred — and recording them as *"one
 * endpoint, two paths"* was wrong, which is how this section came to be missing and how the code came to
 * be written down as unavailable (FB-C65).
 *
 * **The link, not the code, is what gets shared.** `ReferralService` builds it as
 * `publicUrl + "/?ref=" + code`, so the friend arrives with it applied rather than having to be told
 * where to type six characters.
 */

export interface ReferralOffer {
  code: string
  link: string
  discountType: 'percent' | 'fixed'
  discountValue: number
  rewardPoints: number
}

export function referralOfferOf(payload: unknown): ReferralOffer | null {
  if (typeof payload !== 'object' || payload === null) return null
  const row = payload as Record<string, unknown>
  const code = typeof row.code === 'string' ? row.code.trim() : ''
  // No code, no offer. A share button for a code nobody was given shares nothing.
  if (code === '') return null
  const link = typeof row.link === 'string' ? row.link.trim() : ''
  return {
    code,
    // Falling back to the code alone would produce a link that 404s, so an absent one is empty and the
    // button copies the code instead.
    link,
    discountType: row.discountType === 'fixed' ? 'fixed' : 'percent',
    discountValue: typeof row.discountValue === 'number' ? row.discountValue : 0,
    rewardPoints: whole(row.rewardPoints),
  }
}

/** `20 %` or `2,00 €` — the friend's half of the deal, in the units the campaign is set in. */
export function referralDiscountLabel(
  offer: ReferralOffer,
  formatMoney: (cents: number) => string,
): string {
  return offer.discountType === 'percent'
    ? `${String(offer.discountValue)} %`
    : formatMoney(Math.round(offer.discountValue * 100))
}

/** What the copy button puts on the clipboard: the link when there is one, the code when there is not. */
export function shareText(offer: ReferralOffer): string {
  return offer.link === '' ? offer.code : offer.link
}
